"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
interface UseClientNavigationOptions<T, TOptions = undefined> {
  /**
   * Initial state value (typically derived from URL/searchParams)
   */
  readonly initialState: T;

  /**
   * Build the target URL from state and optional navigation options
   */
  readonly buildUrl: (state: T, options?: TOptions) => string;

  /**
   * Parse state from the current window URL (for popstate handling)
   */
  readonly parseUrl: () => T;

  /**
   * Determine if we can use pushState for a given target URL.
   * Return true for fast client-side nav, false for router.push
   */
  readonly canUsePushState: (targetUrl: string, options?: TOptions) => boolean;
}
interface UseClientNavigationResult<T, TOptions = undefined> {
  readonly state: T;
  readonly navigate: (newState: T, options?: TOptions & {
    replace?: boolean | undefined;
  }) => void;
}

/**
 * Generic hook for client-side navigation using pushState when possible.
 * Falls back to router.push for cross-zone navigation.
 */
export function useClientNavigation(t0) {
  const $ = _c(13);
  const {
    initialState,
    buildUrl,
    parseUrl,
    canUsePushState
  } = t0;
  const router = useRouter();
  const [state, setState] = useState(initialState);
  let t1;
  let t2;
  if ($[0] !== initialState) {
    t1 = () => {
      setState(initialState);
    };
    t2 = [initialState];
    $[0] = initialState;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== parseUrl) {
    t3 = () => {
      if (globalThis.window === undefined) {
        return;
      }
      const {
        window: browserWindow
      } = globalThis;
      if (!browserWindow) {
        return;
      }
      const onPopState = () => {
        setState(parseUrl());
      };
      browserWindow.addEventListener("popstate", onPopState);
      return () => browserWindow.removeEventListener("popstate", onPopState);
    };
    t4 = [parseUrl];
    $[3] = parseUrl;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== buildUrl || $[7] !== canUsePushState || $[8] !== router) {
    t5 = (newState, options) => {
      if (globalThis.window === undefined) {
        return;
      }
      const {
        window: browserWindow_0
      } = globalThis;
      if (!browserWindow_0) {
        return;
      }
      const target = buildUrl(newState, options);
      const currentUrl = browserWindow_0.location.pathname + browserWindow_0.location.search;
      if (currentUrl === target) {
        setState(newState);
        return;
      }
      if (canUsePushState(target, options)) {
        const method = options?.replace ? "replaceState" : "pushState";
        browserWindow_0.history[method](null, "", target);
        setState(newState);
      } else {
        setState(newState);
        if (options?.replace) {
          router.replace(target);
        } else {
          router.push(target);
        }
      }
    };
    $[6] = buildUrl;
    $[7] = canUsePushState;
    $[8] = router;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const navigate = t5;
  let t6;
  if ($[10] !== navigate || $[11] !== state) {
    t6 = {
      state,
      navigate
    };
    $[10] = navigate;
    $[11] = state;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}